import {Component, Input} from '@angular/core';

/**
 * The plural counts under a city's name, kept in components of their own and left out of
 * hydration.
 *
 * An ICU expression repeated by an `@for` does not survive i18n hydration in Angular 21.2: every
 * card after the first keeps the case the client did not pick as well as the one it did, and the
 * text renders twice ("6 communities6 communities"). Hosting the ICU in a child component is not
 * enough — the instances share one serialized payload — so these render client-side instead. They
 * are two short lines of text, so the cost is a repaint of a few words, while the rest of the page
 * hydrates and stays put.
 *
 * The message ids are the ones the templates used before, so every existing translation still
 * applies; keep them if this markup moves again.
 */
@Component({
    selector: 'app-city-chip-count',
    host: {'ngSkipHydration': ''},
    template: `
        @if (communities > 0) {
            <ng-container i18n="@@cities-card-communities">{communities, plural, =1 {1 community} other {{{communities}} communities}}</ng-container>
        } @else {
            <!-- A city can be on the list for its game nights alone; "0 communities" would undersell it. -->
            <ng-container i18n="@@cities-card-events">{events, plural, =1 {1 event} other {{{events}} events}}</ng-container>
        }
    `,
})
export class CityChipCountComponent {
    @Input({required: true}) communities!: number;
    @Input({required: true}) events!: number;
}

@Component({
    selector: 'app-city-card-counts',
    host: {'ngSkipHydration': ''},
    template: `
        @if (communities > 0) {
            <span i18n="@@cities-card-communities">{communities, plural, =1 {1 community} other {{{communities}} communities}}</span>
            <span aria-hidden="true"> · </span>
        }
        <span i18n="@@cities-card-events">{events, plural, =1 {1 event} other {{{events}} events}}</span>
    `,
})
export class CityCardCountsComponent {
    @Input({required: true}) communities!: number;
    @Input({required: true}) events!: number;
}
